import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RegistrationMarriageFormVM } from 'mutation/view-models/registration-marriage';
import { ResponseAnprSaveData, SubjectsList } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { PmiButton } from 'shell/components/shared/button';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import ExitModal from 'mutation/components/shared/exit-modal';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerRequestRegistrationMarriageData } from 'mutation/utils/transformer-request-registration-marriage';
import { toast } from 'react-toastify';
import { validationSchema } from 'mutation/constants/registration-marriage-validation';
import { RegistrationMarriageNonResidentCrumbs } from 'mutation/constants/registration-marriage-non-resident-crumbs';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
// import ErrorModal from 'mutation/components/shared/error-modal';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import PmiTitle from 'shell/components/shared/title';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import RegistrationMarriageNonResident from 'mutation/components/registration-marriage-non-resident';
import { transformerRegistrationMarriageNonResidentData } from 'mutation/utils/transformer-registration-marriage-non-resident';

export interface RegistrationMarriageNonResidentPageProps {
  isWithoutBreadcrumbs?: boolean;
  onHandleExit?: () => void;
  onConfirm?: () => void;
  initialValues?: RegistrationMarriageFormVM;
  disabledFromAct?: boolean;
  goBackUrl?: string;
  editSipoUrl?: string;
}

const RegistrationMarriageNonResidentPage = ({
  isWithoutBreadcrumbs = false,
  onHandleExit,
  onConfirm,
  initialValues = {},
  goBackUrl = '/mutazioni/persona/matrimonio/non-residenti',
  editSipoUrl,
}: RegistrationMarriageNonResidentPageProps): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);
  // const [openError, setOpenError] = useState<boolean>(false);

  const params = useParams();

  const { idIntestatario, idSchedaSoggettoANPR } = params;

  const [anpr] = useHttpFetch<HttpResponse<SubjectsList>, RegistrationMarriageFormVM>(
    `${__PERSON_API_URL__}/non-residenti/soggetto`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          matricolaSipo: idIntestatario || undefined,
          idSchedaSoggettoAnpr: idSchedaSoggettoANPR || undefined,
        },
      },
      transformer: transformerRegistrationMarriageNonResidentData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseMarriage, registerMarriage] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/non-residenti/registrazione-matrimonio`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseMarriage.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (values) => {
      const data = transformerRequestRegistrationMarriageData(values);

      (
        registerMarriage({ data }) as ResponsePromise<
          HttpResponse<ResponseAnprSaveData>,
          SaveDataAnpr
        >
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        if (onConfirm) {
          onConfirm();
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate(goBackUrl);
      });
    },
    [registerMarriage, navigate]
  );

  const handleExit = useCallback(() => {
    navigate(goBackUrl);
  }, [navigate]);

  const handleOpenModal = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  // useEffect(() => {
  //   const data = anpr.data as RegistrationMarriageFormVM;

  //   const canBeMutated = checkPersonRegistrationMarriage(data?.civilStatus?.oldStatus);

  //   if (!canBeMutated) {
  //     // persona già coniugata
  //     setOpenError(true);
  //   }
  // }, [anpr.data]);

  // const onCloseErrorModal = useEventCallback((value: boolean) => {
  //   setOpenError(value);
  //   navigate(goBackUrl);
  // });

  const isLoading = !anpr.data || anpr.isLoading;

  return !isLoading ? (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : (
        <Breadcrumbs items={RegistrationMarriageNonResidentCrumbs} />
      )}
      <PmiTitle title="Registrazione Matrimonio Non Residenti" />
      <Formik
        validationSchema={validationSchema}
        initialValues={{ ...(anpr.data || {}), ...initialValues } as RegistrationMarriageFormVM}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <StickyDiv>
                <StepButton active fullWidth>
                  <StepTypography active>Stato Civile</StepTypography>
                </StepButton>
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      fullWidth
                      type="submit"
                      id="confirm"
                    >
                      Conferma
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      fullWidth
                      type="reset"
                      id="reset"
                    >
                      Ripulisci
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrand"
                      variant="contained"
                      fullWidth
                      onClick={handleOpenModal}
                      id="deny-operation"
                    >
                      Annulla Operazione
                    </PmiButton>
                  </div>
                </div>
              </StickyDiv>
            </Grid>
            <Grid item xs={12} md={10}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={12}>
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorMessage.title || ''}
                    messages={errorMessage.messages || []}
                    visibility={errorMessage.visibility || false}
                    onChangeVisibility={onVisibility}
                  />
                  <SearchMessages
                    type={MessageType.Warning}
                    title={
                      castedAnprData?.warnings?.length === 1
                        ? `C'è 1 warning`
                        : `Ci sono ${castedAnprData?.warnings?.length} warning`
                    }
                    messages={castedAnprData?.warnings || []}
                    visibility={
                      visibility.warning !== undefined
                        ? visibility.warning
                        : (castedAnprData?.warnings || [])?.length > 0
                    }
                    onChangeVisibility={onVisibility}
                  />
                  <RegistrationMarriageNonResident editSipoUrl={editSipoUrl} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <ExitModal
            open={open}
            onClose={() => setOpen(false)}
            onConfirm={() => {
              if (onHandleExit) {
                onHandleExit();
                return;
              }
              handleExit();
            }}
          />
          {/* <ErrorModal open={openError} onClose={onCloseErrorModal} /> */}
        </Form>
      </Formik>
    </Fragment>
  ) : (
    <></>
  );
};

RegistrationMarriageNonResidentPage.displayName =
  'Registration Marriage Non resident page component';

export default RegistrationMarriageNonResidentPage;
